import get from 'lodash/get';
import { bugsnagError } from 'services/bugsnag';
import { PROJECT_SUB_LABELS } from 'web-ui-kit/dataHeader';
import { trimProjectName } from 'web-ui-kit/utils';
import { SHARED_VIEW_TYPE } from 'utils/constants';

import {
  navigateToProjectPageFor,
  navigateToExpressRoomDetails
} from 'services/routing';

import {
  getNumDesignsNotViewed,
  getSpaceStatus,
  spaceHasLayouts
} from './ProjectPpoAPI';

const PROJECT_PLACEHOLDER = 'Project';

/**
* Main method to get user projects.
* @return array of projects
*/
export function getProjects ({
  allConversations = {},
  allSpaces = []
} = {}) {
  const spaces = convertSpaces(allSpaces);

  const convertedProjects = setProjects({ allConversations, spaces });

  return [
    ...convertedProjects
    // REMOVED AS PART OF MODSY WIND DOWN WORK
    // Let's add the cell to Start New Project
    // { id: START_NEW_PROJECT_ID }
  ];
}

function convertSpaces (spaces) {
  /**
   * In some cases the spaces have a different structure without the fields property.
   * Example: When user goes directly to a PPO page, spaces are built from spaceList.
   */
  const isFromSpaceList = spaces[0] && spaces[0].isFromSpaceList;

  if (!isFromSpaceList) {
    return spaces.map(space => space.toLegacy());
  }

  // At this point spaces come from spaceList and have a different structure.
  // Let's convert them to a data structure equivalent to space.toLegacy().
  return spaces.map(space => {
    const {
      designs,
      processed,
      showroom
    } = space;

    return {
      ...space,
      isLayoutSpace: spaceHasLayouts({ designs }),
      nonViewedDesignsCount: getNumDesignsNotViewed({ designs }),
      status: getSpaceStatus({ processed }),
      isShowroomSpace: showroom
    };
  });
}

/**
* Method to convert spaces into user projects.
* @return array of projects
*/
function setProjects ({
  allConversations = {},
  spaces = []
}) {
  return spaces.map((space) => {
    const {
      id,
      inProgressSubStatus,
      isLayoutSpace,
      isShowroomSpace,
      name,
      nonViewedDesignsCount,
      status
    } = space;

    const trimmedName = trimProjectName(name);
    const unreadMessageCount = get(allConversations, `[${id}].unreadMessageCount`, 0);

    return ({
      id,
      name: trimmedName || PROJECT_PLACEHOLDER,
      status: computeSubtitleFor({
        inProgressSubStatus,
        isLayoutSpace,
        nonViewedDesignsCount,
        spaceStatus: status,
        unreadMessageCount
      }),
      projectDetails: {
        isLayoutSpace,
        spaceStatus: status,
        spaceId: id,
        isShowroomSpace,
        isUserProject: true
      }
    });
  });
}

/**
* Callback fired when user selects a project.
*/
export function onChangeSpace ({
  analyticsInfo = {},
  isAdmin = false,
  isLayoutSpace = false,
  isShowroomSpace = false,
  onSpaceChange = () => {},
  spaceId = null,
  spaceStatus = '',
  userId = null
} = {}) {
  const { design, studio, trackSpaceSelectorNavigate } = analyticsInfo;
  // fire analytics
  trackSpaceSelectorNavigate({
    design,
    spaceIdSelected: spaceId,
    studio,
    userId
  });

  // 1. If the space is ready, or is a layout space, navigate to Studio.
  if (spaceStatus === SPACE_STATUS.READY || isLayoutSpace) {
    const routingArgs = { spaceId };

    // go to shared version of studio if auth user does not own studio
    if (studio && !isAdmin && (studio.userId() !== userId)) {
      routingArgs.viewType = SHARED_VIEW_TYPE;
    }

    navigateToProjectPageFor(routingArgs);

    if (typeof onSpaceChange === 'function') {
      onSpaceChange();
    }
    return;
  }

  // 2. Handle showroom PPO.
  if (isShowroomSpace) {
    navigateToExpressRoomDetails({
      spaceId,
      search: ''
    });
    return;
  }

  // 3. At this point, the space is not ready, we need to redirect to
  // the project page
  try {
    navigateToProjectPageFor({ spaceId });
  } catch (error) {
    bugsnagError(error, { spaceId });
  }
}

/**
* To know if layout is selected.
* @return boolean
*/
function spaceLayoutNotSelected (subStatus = '') {
  const layoutSubmittedSubStatus = 'styling_in_progress';
  return subStatus !== layoutSubmittedSubStatus;
}

/**
* To get the project sublabel for our dropdown "My Projects".
* @return string
*/
export function computeSubtitleFor ({
  inProgressSubStatus = '',
  isLayoutSpace = false,
  nonViewedDesignsCount = null,
  spaceStatus = '',
  unreadMessageCount = 0
} = {}) {
  const layoutNotSelected = spaceLayoutNotSelected(inProgressSubStatus);
  const layoutNeedsToBeSelected = isLayoutSpace && layoutNotSelected;

  if (unreadMessageCount > 0) {
    return PROJECT_SUB_LABELS.NEW_MESSAGE;
  } else if (layoutNeedsToBeSelected) {
    return PROJECT_SUB_LABELS.SELECT_LAYOUT;
  } else if (spaceStatus === SPACE_STATUS.READY) {
    if (nonViewedDesignsCount === 1) {
      return PROJECT_SUB_LABELS.ONE_NEW_DESIGN_READY;
    } else if (nonViewedDesignsCount > 1) {
      return PROJECT_SUB_LABELS.SOME_NEW_DESIGNS_READY;
    } else {
      return PROJECT_SUB_LABELS.DESIGN_READY;
    }
  } else if (isLayoutSpace) {
    return PROJECT_SUB_LABELS.WORKING_ON_DESIGNS;
  } else if (spaceStatus === SPACE_STATUS.REVIEW) {
    return PROJECT_SUB_LABELS.REVIEWING_PROJECT;
  } else if (
    spaceStatus === SPACE_STATUS.PENDING ||
    spaceStatus === SPACE_STATUS.PRE_PURCHASE
  ) {
    return PROJECT_SUB_LABELS.TELL_US_MORE;
  } else if (
    spaceStatus === SPACE_STATUS.DESIGNS_PENDING ||
    spaceStatus === SPACE_STATUS.IN_PROGRESS
  ) {
    return PROJECT_SUB_LABELS.WORKING_ON_PROJECT;
  } else {
    // This should never happen
    return PROJECT_SUB_LABELS.WORKING_ON_PROJECT;
  }
}

/**
* Mapping of our FE space statuses.
*/
const SPACE_STATUS = {
  PENDING: 'pending',
  READY: 'ready',
  REVIEW: 'under modsy review',
  DESIGNS_PENDING: 'initial designs pending',
  PRE_PURCHASE: 'pre-purchase',
  IN_PROGRESS: 'inprogress',
  ABANDONED: 'abandoned',
  UNKNOWN: 'unknown'
};
